import * as React from 'react';
import {Avatar, Button, Switch, ToggleButton, ToggleButtonGroup} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import FavoriteIcon from '@mui/icons-material/Favorite';
import StarIcon from '@mui/icons-material/Star';
import HistoryIcon from '@mui/icons-material/History';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {useDispatch} from 'react-redux';
import {useAuth} from '../../auth/AuthContext';
import {useUserPlaylists} from '../../store/slices/userPlaylists/userPlaylistsSlice';
import {useFavorites} from '../../store/slices/favorites/favoritesSlice';
import {useLikedSongs} from '../../store/slices/likedSongs/likedSongsSlice';
import {useUserSelector} from '../../store/slices/user/userSlice';
import {useSettings} from '../../store/slices/settings/settingsSlice';
import {
    setAutoplayOnLaunch,
    setDefaultPlaylistVisibility,
    setReducedMotion,
} from '../../store/slices/settings/reducers';
import {PlaylistVisibility} from '../../store/slices/userPlaylists/reducers';
import styles from './Profile.module.scss';

export const Profile: React.FC = () => {
    const {user, status, promptLogin, signOut} = useAuth();

    if (status === 'loading') {
        return <div className={styles.profile}><div className={styles.profile__skeleton}/></div>;
    }

    return (
        <div className={styles.profile}>
            {user ? <SignedInView/> : <GuestView onSignIn={() => promptLogin()}/>}
            {user && (
                <div className={styles.profile__signOutWrapper}>
                    <Button
                        variant="text"
                        startIcon={<LogoutIcon/>}
                        onClick={() => signOut()}
                        className={styles.profile__signOutBtn}
                    >
                        Sign out
                    </Button>
                </div>
            )}
        </div>
    );
};

const GuestView: React.FC<{onSignIn: () => void}> = ({onSignIn}) => (
    <>
        <div className={styles.profile__hero}>
            <div className={styles.profile__heroIcon}><AutoAwesomeIcon/></div>
            <div className={styles.profile__heroTitle}>Make it yours</div>
            <div className={styles.profile__heroSubtitle}>
                Sign in to sync your library across devices, share playlists, and pick up where you left off.
            </div>
            <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<LoginIcon/>}
                onClick={onSignIn}
                className={styles.profile__heroBtn}
            >
                Sign in or create an account
            </Button>
        </div>

        <div className={styles.profile__perks}>
            <Perk icon={<CloudSyncIcon/>} title="Sync everywhere" desc="Your liked songs, favorites, and playlists follow you to every device."/>
            <Perk icon={<PublicIcon/>} title="Share your taste" desc="Make playlists public and share them with anyone via link."/>
            <Perk icon={<HistoryIcon/>} title="Pick up later" desc="Recently played stays right where you need it."/>
        </div>

        <SettingsSection/>
    </>
);

const SignedInView: React.FC = () => {
    const {user} = useAuth();
    const playlists = useUserPlaylists();
    const favorites = useFavorites();
    const likedSongs = useLikedSongs();
    const {recentlyPlayed} = useUserSelector(['recentlyPlayed']);

    const displayName = user!.displayName || user!.email?.split('@')[0] || 'Listener';
    const initial = displayName.charAt(0).toUpperCase();
    const recentlyPlayedCount = Object.keys(recentlyPlayed ?? {}).length;

    return (
        <>
            <div className={styles.profile__header}>
                <Avatar
                    src={user!.photoURL || undefined}
                    alt={displayName}
                    className={styles.profile__avatar}
                >
                    {initial}
                </Avatar>
                <div className={styles.profile__identity}>
                    <div className={styles.profile__name}>{displayName}</div>
                    {user!.email && <div className={styles.profile__email}>{user!.email}</div>}
                </div>
            </div>

            <div className={styles.profile__stats}>
                <Stat icon={<LibraryMusicIcon/>} label="Playlists" value={playlists.length}/>
                <Stat icon={<FavoriteIcon/>} label="Liked songs" value={likedSongs.length}/>
                <Stat icon={<StarIcon/>} label="Favorites" value={favorites.length}/>
                <Stat icon={<HistoryIcon/>} label="Recently played" value={recentlyPlayedCount}/>
            </div>

            <SettingsSection/>
        </>
    );
};

const SettingsSection: React.FC = () => {
    const dispatch = useDispatch();
    const {defaultPlaylistVisibility, reducedMotion, autoplayOnLaunch} = useSettings();

    const handleVisibilityChange = (_: unknown, value: PlaylistVisibility | null) => {
        if (value) dispatch(setDefaultPlaylistVisibility(value));
    };

    return (
        <div className={styles.profile__section}>
            <div className={styles.profile__sectionTitle}>Settings</div>

            <div className={styles.profile__setting}>
                <div className={styles.profile__settingHead}>
                    <div className={styles.profile__settingIcon}><LockIcon/></div>
                    <div className={styles.profile__settingText}>
                        <div className={styles.profile__settingLabel}>New playlists are</div>
                        <div className={styles.profile__settingDesc}>
                            Public playlists can be opened by anyone with the link.
                        </div>
                    </div>
                </div>
                <ToggleButtonGroup
                    exclusive
                    value={defaultPlaylistVisibility}
                    onChange={handleVisibilityChange}
                    size="small"
                    className={styles.profile__toggleGroup}
                    aria-label="Default playlist visibility"
                >
                    <ToggleButton value="private" aria-label="Private">
                        <LockIcon fontSize="small"/>&nbsp;Private
                    </ToggleButton>
                    <ToggleButton value="public" aria-label="Public">
                        <PublicIcon fontSize="small"/>&nbsp;Public
                    </ToggleButton>
                </ToggleButtonGroup>
            </div>

            <SettingRow
                icon={<PlayArrowIcon/>}
                label="Autoplay on launch"
                desc="Resume the last song automatically when the app opens."
                checked={autoplayOnLaunch}
                onChange={v => dispatch(setAutoplayOnLaunch(v))}
            />
            <SettingRow
                icon={<AutoAwesomeIcon/>}
                label="Reduce motion"
                desc="Disable the animated background. Easier on the eyes and on battery."
                checked={reducedMotion}
                onChange={v => dispatch(setReducedMotion(v))}
            />
        </div>
    );
};

const SettingRow: React.FC<{
    icon: React.ReactNode;
    label: string;
    desc: string;
    checked: boolean;
    onChange: (value: boolean) => void;
}> = ({icon, label, desc, checked, onChange}) => (
    <div className={styles.profile__setting}>
        <div className={styles.profile__settingHead}>
            <div className={styles.profile__settingIcon}>{icon}</div>
            <div className={styles.profile__settingText}>
                <div className={styles.profile__settingLabel}>{label}</div>
                <div className={styles.profile__settingDesc}>{desc}</div>
            </div>
        </div>
        <Switch checked={checked} onChange={e => onChange(e.target.checked)} color="secondary"/>
    </div>
);

const Stat: React.FC<{icon: React.ReactNode; label: string; value: number}> = ({icon, label, value}) => (
    <div className={styles.profile__stat}>
        <div className={styles.profile__statIcon}>{icon}</div>
        <div className={styles.profile__statValue}>{value}</div>
        <div className={styles.profile__statLabel}>{label}</div>
    </div>
);

const Perk: React.FC<{icon: React.ReactNode; title: string; desc: string}> = ({icon, title, desc}) => (
    <div className={styles.profile__perk}>
        <div className={styles.profile__perkIcon}>{icon}</div>
        <div className={styles.profile__perkText}>
            <div className={styles.profile__perkTitle}>{title}</div>
            <div className={styles.profile__perkDesc}>{desc}</div>
        </div>
    </div>
);
